import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { PACKAGES } from '../config/packages';
import SignaturePad from '../components/SignaturePad';
import { 
  CheckCircle, 
  XCircle, 
  Calendar, 
  Building, 
  User, 
  FileText,
  AlertCircle,
  Loader2,
  Clock,
  Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OfferLandingPage = () => {
  const { offerId } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signature, setSignature] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    fetchOffer();
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      const response = await fetch(`${API_URL}/api/offers/${offerId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Angebot nicht gefunden');
        } else {
          setError('Fehler beim Laden des Angebots');
        }
        return;
      }
      
      const data = await response.json();
      setOffer(data);
      
      // Check if already processed
      if (data.status === 'accepted' || data.status === 'declined') {
        setSubmitted(true);
        setSubmitResult(data.status);
      }
    } catch (err) {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (accepted) => {
    if (accepted && !signature) {
      alert('Bitte unterschreiben Sie das Angebot');
      return;
    }
    
    if (accepted && !privacyAccepted) {
      alert('Bitte akzeptieren Sie die Datenschutzerklärung');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/offers/${offerId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature: accepted ? signature : '',
          accepted
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setSubmitResult(accepted ? 'accepted' : 'declined');
      } else {
        const data = await response.json();
        alert(`Fehler: ${data.detail}`);
      }
    } catch (err) {
      alert('Verbindungsfehler');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(price);
  };

  const selectedPackage = offer?.package_type ? PACKAGES[offer.package_type] : null;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-600 mx-auto mb-4" />
          <p className="text-gray-600">Angebot wird geladen...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Fehler</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">Zur Startseite</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-12">
            {submitResult === 'accepted' ? (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Vielen Dank!</h2>
                <p className="text-gray-600 mb-6">
                  Sie haben das Angebot angenommen. Wir werden uns in Kürze bei Ihnen melden.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-gray-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Angebot abgelehnt</h2>
                <p className="text-gray-600 mb-6">
                  Schade! Falls Sie es sich anders überlegen, kontaktieren Sie uns gerne.
                </p>
              </>
            )}
            <Link to="/">
              <Button className="bg-gradient-to-r from-cyan-600 to-purple-600">
                Zu Alpen Pro Suisse
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if offer is still valid
  const isExpired = new Date(offer.valid_until) < new Date();
  const canSign = offer.status === 'sent' && !isExpired;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/images/alpen-pro-logo-new.png" 
            alt="Alpen Pro Suisse" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Ihr individuelles Angebot
          </h1>
          <p className="text-gray-600 mt-2">von Alpen Pro Suisse</p>
        </div>

        {/* Expired Warning */}
        {isExpired && (
          <Card className="mb-6 border-red-300 bg-red-50">
            <CardContent className="py-4 flex items-center gap-3">
              <Clock className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-700">Angebot abgelaufen</p>
                <p className="text-sm text-red-600">
                  Dieses Angebot war gültig bis {formatDate(offer.valid_until)}. 
                  Kontaktieren Sie uns für ein aktuelles Angebot.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Info */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{offer.customer_name}</h2>
                {offer.company_name && (
                  <p className="text-gray-600 flex items-center gap-2 mt-1">
                    <Building className="w-4 h-4" />
                    {offer.company_name}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offer Details */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex items-center gap-3 mb-6">
              {offer.offer_type === 'package' ? (
                <Package className="w-6 h-6 text-cyan-600" />
              ) : (
                <FileText className="w-6 h-6 text-cyan-600" />
              )}
              <h3 className="text-lg font-semibold text-gray-900">Angebotsdetails</h3>
            </div>

            {/* Package Info */}
            {selectedPackage && (
              <div className={`p-4 rounded-lg bg-gradient-to-br ${selectedPackage.color} text-white mb-6`}>
                <h4 className="text-xl font-bold mb-2">
                  {selectedPackage.emoji} {selectedPackage.name} Paket
                </h4>
                <p className="opacity-90">{selectedPackage.description}</p>
              </div>
            )}

            {/* Description or Package Features */}
            {offer.offer_type === 'free-text' ? (
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-2">Leistungsbeschreibung:</h4>
                <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-gray-700">
                  {offer.description}
                </div>
              </div>
            ) : selectedPackage && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Enthaltene Leistungen:</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  {selectedPackage.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                      <span>{feature.icon}</span>
                      <span className="text-gray-600">{feature.title}: {feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price */}
            <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg mb-4">
              <span className="text-gray-700 font-medium">Angebotspreis:</span>
              <span className="text-3xl font-bold text-cyan-600" data-testid="offer-price">
                {formatPrice(offer.price)}
              </span>
            </div>

            {/* Validity */}
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-5 h-5" />
              <span>Gültig bis: <strong>{formatDate(offer.valid_until)}</strong></span>
            </div>

            {/* Agent Notes */}
            {offer.agent_notes && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Hinweise:</h4>
                <p className="text-yellow-700 text-sm">{offer.agent_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section - Only if can sign */}
        {canSign && (
          <Card className="mb-6">
            <CardContent className="py-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Digitale Unterschrift
              </h3>
              
              <SignaturePad 
                onSave={setSignature} 
                onClear={() => setSignature('')}
              />

              {/* Privacy Consent */}
              <div className="flex items-start gap-3 mt-6 p-4 bg-gray-50 rounded-lg">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  data-testid="privacy-checkbox"
                />
                <label htmlFor="privacy" className="text-sm text-gray-600 cursor-pointer">
                  Ich stimme der Verarbeitung meiner Daten gemäss{' '}
                  <Link 
                    to="/datenschutz" 
                    target="_blank" 
                    className="text-cyan-600 hover:underline"
                  >
                    Datenschutzerklärung (DSG)
                  </Link>
                  {' '}zu.
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <Button
                  onClick={() => handleSign(false)}
                  variant="outline"
                  disabled={submitting}
                  className="flex-1"
                  data-testid="decline-offer-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Angebot ablehnen
                </Button>
                <Button
                  onClick={() => handleSign(true)}
                  disabled={submitting || !signature || !privacyAccepted}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700"
                  data-testid="accept-offer-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  {submitting ? 'Wird verarbeitet...' : 'Angebot annehmen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <div className="text-center text-gray-500 text-sm">
          <p>Fragen? Kontaktieren Sie uns:</p>
          <p className="mt-1">
            <a href="tel:+41762652627" className="text-cyan-600 hover:underline">
              +41 76 265 26 27
            </a>
            {' | '}
            <a href="mailto:info@alpenpro.ch" className="text-cyan-600 hover:underline">
              info@alpenpro.ch
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OfferLandingPage;
